import { MultiStepFormConfig, BaseFormData } from './components/types';
import { CriteriaStep, SingleSelectionStep, AbnLookupStep, BusinessDetailsStep, TrustDetailsStep, TextFieldStep, ContactDetailsStep, CheckboxListStep } from './steps';
import { createFormOptions } from './steps/baseFormOptions';
import { createFormValidations } from './steps/baseFormValidations';
import { 
  criteriaStepInitialData, 
  singleSelectionStepInitialData, 
  abnLookupStepInitialData, 
  businessDetailsStepInitialData,
  trustDetailsStepInitialData,
  textFieldStepInitialData, 
  contactDetailsStepInitialData,
  combineStepData 
} from './steps/initialData';
import { getUtmParamsForDynamics } from '@/lib/utm-tracking';

// Use the base FormData interface - no need for duplicate interface

// Form options and validations - centralized in config for easy management
export const formOptions = createFormOptions({
  suitabilityCriteria: [
    "Is registered in Australia with an ABN",
    "Has a minimum of $1M annual turnover",
    "Has $100,000+ in receivables",
    "Can provide evidence of debt"
  ],
  financingTypes: [
    {
      value: "invoice-discounting",
      title: "Invoice Discounting",
      description: "Ideal for established businesses. Confidential or disclosed facility where you keep control of collections and relationships."
    },
    {
      value: "invoice-factoring",
      title: "Invoice Factoring",
      description: "Ideal for startups or growing businesses. Disclosed via an assignment note only. We never contact your customers and you always stay in control."
    },
    {
      value: "not-sure",
      title: "I'm not sure",
      description: "Help me understand what's best."
    }
  ],
});

export const formValidations = createFormValidations({
  // Invoice Finance specific validations
  financingTypeSelection: (data: BaseFormData) => !!data.financingType,
  businessDetailsStep: (data: BaseFormData) => {
    // Basic validation for business details
    const hasBasicFields = !!(data.countryOfRegistration && data.businessType && data.numberOfEmployees && data.annualTurnover && data.outstandingInvoiceValue && data.numberOfCustomers);
    
    const stateValid = data.countryOfRegistration === "Other" ? true : !!(data.state && data.state !== 'Select');

    // Industry validation: if "Other" is selected, customIndustry is required; otherwise industry is required
    const industryValid = data.industry === "Other" 
      ? !!(data.customIndustry && typeof data.customIndustry === 'string' && data.customIndustry.trim().length > 0)
      : !!(data.industry && data.industry !== 'Select');
    
    return hasBasicFields && industryValid && stateValid;
  },
  addonServicesValidation: (data: BaseFormData) => (data.addonServices as string[])?.length > 0,
  trustDetailsStep: (data: BaseFormData) => {
    // Validation for trust details - only required if business type is Trust
    if (data.businessType !== 'Trust') return true;
    
    // Basic required fields
    const hasBasicFields = !!(data.trusteeType && data.trusteeName);
    
    // ABN/ACN only required if trustee is Corporate Entity
    const needsAbn = data.trusteeType === 'Corporate Entity';
    const hasAbn = needsAbn ? !!data.trustAbnAcn : true;
    
    return hasBasicFields && hasAbn;
  },
});

// Combine initial data from all steps
const initialFormData = combineStepData(
  criteriaStepInitialData,
  singleSelectionStepInitialData,
  abnLookupStepInitialData,
  businessDetailsStepInitialData,
  trustDetailsStepInitialData,
  textFieldStepInitialData,
  contactDetailsStepInitialData,
  { addonServices: [] } // Initialize checkbox list field
);

export const invoiceFinanceEnquireFormConfig: MultiStepFormConfig<BaseFormData> = {
  formId: 'invoice-finance-enquire',
  formOptions: formOptions, // Pass form options globally
  steps: [
    {
      id: 'suitability-check',
      title: 'Quick suitability check.',
      description: 'Let\’s check a few basics to make sure <strong>Debtor & Invoice Finance</strong> is the right fit for your business.',
      criteriaQuestion: "Does your business meet the below criteria?",
      component: CriteriaStep,
      validation: () => true, // No validation needed for this step
    },
    {
      id: 'preference',
      title: 'What\’s your preference?',
      description: 'Choose the approach that fits your business. We\’ll tailor our follow-up based on what you choose.',
      component: SingleSelectionStep,
      validation: formValidations.createSingleSelectionValidation('financingType'),
      stepConfig: {
        fieldName: 'financingType',
        optionsKey: 'financingTypes',
        title: "What\’s your preferred type of invoice financing?",
        description: undefined
      }
    },
    {
      id: 'business-search',
      title: 'Tell us about your business.',
      description: 'Start typing your ABN, ACN, or business name. We\’ll fill in the rest.',
      component: AbnLookupStep,
      validation: formValidations.abnLookupStep,
    },
    {
      id: 'business-metrics',
      title: 'Confirm your business details.',
      description: 'These details help us understand your structure and business needs.',
      component: BusinessDetailsStep,
      stepConfig: {
        showTradingName: true,
        showCountryOfRegistration: true,
        showState: true,
        showBusinessType: true,
        showIndustry: true,
        showNumberOfEmployees: true,
        showNumberOfCustomers: true,
        showOutstandingInvoiceValue: true,
        showAnnualTurnover: true,
        turnoverAlert: {
          triggerValues: ["$0 - $999,999"],
          message: "Debtor & Invoice Finance is usually available for businesses with <strong>annual turnover above $1 million</strong>.",
          className: "bg-gray-100 p-0 border-0"
        }
      },
      validation: formValidations.businessDetailsStep,
    },
    {
      id: 'trust-details',
      title: 'Tell us more about your trust.',
      description: 'We ask this because finance contracts are held by the trustee on behalf of the trust.',
      component: TrustDetailsStep,
      stepConfig: {
        showTrustType: false,
        showTrusteeType: true,
        showAbnAcn: true,
        showTrusteeName: true,
      },
      validation: formValidations.trustDetailsStep,
      shouldShow: (data: BaseFormData) => data.businessType === 'Trust',
    },
    {
      id: 'business-description',
      title: 'Describe your business.',
      description: 'This helps our team of finance specialists better understand your business needs.',
      component: TextFieldStep,
      validation: formValidations.textFieldStep,
      isOptional: true,
    },
    {
      id: 'addon-services',
      title: 'Enhance your facility with add-ons.',
      description: 'Octet offers powerful add-ons that enhance your facility. Your preferences help our team tailor the discussion to your business needs.',
      component: CheckboxListStep,
      validation: formValidations.addonServicesValidation,
      stepConfig: {
        title: 'Would you like our team to tell you more?',
        fieldName: 'addonServices',
        options: [
          { value: 'term-loan', label: 'I\'m interested in the Octet Term Loan', description: 'Access up to $5M in additional working capital.' },
          { value: 'buyer-protection', label: 'I\'m interested in Octet Buyer Protection', description: 'Safeguard your business against customer non-payment.' },
          { value: 'not-sure', label: 'Not at this stage', description: 'You can always enquire about this at a later stage.', clearOtherOptions: true }
        ],
        isRequired: true
      }
    },
    {
      id: 'contact-details',
      title: 'Your contact details.',
      description: '<strong>How can we reach you?</strong> We\’ll get in touch within 1 business day to guide you through the next steps.',
      component: ContactDetailsStep,
      validation: formValidations.contactDetailsStep,
    },
  ],
  initialData: initialFormData,
  thankYouPage: {
    title: 'Thanks for your enquiry',
    subtitle: '<strong>We\’ve received your details.</strong> We will be in touch shortly to discuss your business needs and next steps.',
    firstName: '{{firstName}}', // Will be replaced with actual user's first name
    nextStepsTitle: 'Here\’s what\’s next for',
    businessName: '{{businessName}}', // Will be replaced with actual business name
    nextStepsItems: [
      {
        icon: 'schedule',
        text: 'Our team will contact you within <strong>1 working day</strong>',
      },
      {
        icon: 'mail',
        text: 'You\’ll receive an <strong>email or a phone call</strong> from our team.'
      },
      {
        icon: 'how_to_reg',
        text: 'We\’ll walk you through the process and <strong>answer any questions</strong>.'
      }
    ],
    ctaButton: {
      text: 'View Homepage',
      href: '/'
    },
    additionalInfo: 'In the meantime, stay in-the-know with helpful trends, insights and business guides.',
    socialButton: {
      text: 'Follow Octet on LinkedIn',
      href: 'https://www.linkedin.com/company/octet-finance'
    }
  },
  onSubmit: async (data: BaseFormData) => {
    try {
      // Get UTM parameters from sessionStorage
      const utmParams = typeof window !== 'undefined' ? getUtmParamsForDynamics() : {
        ot_utmsource: '',
        ot_utmmedium: '',
        ot_utmterm: '',
        ot_utmcampaign: '',
      };

      // Map form data to Dynamics field format
      const dynamicsPayload = {
        ot_topic: 'Debtor Finance application',
        pb_title: (data.title as string) || '',
        ot_firstname: (data.firstName as string) || '',
        ot_lastname: (data.lastName as string) || '',
        ot_phone: (data.phone as string) || '',
        ot_workemail: (data.workEmail as string) || '',
        // Business details
        ot_country: (data.countryOfRegistration as string) || '',
        pb_postcode: (data.postcode as string) || '',
        ot_state: (data.state as string) || '',
        ot_businesstype: (data.businessType as string) || '',
        ot_industry: (data.industry === 'Other' ? (data.customIndustry as string) || '' : (data.industry as string) || ''),
        pb_numberofemployees: (data.numberOfEmployees as string) || '',
        ot_customersonledger: (data.numberOfCustomers as string) || '',
        ot_receivableledgervalue: (data.outstandingInvoiceValue as string) || '',
        ot_annualrevenue: (data.annualTurnover as string) || '',
        ot_businessname: (data.businessName as string) || '',
        ot_abn: (data.selectedABN as string) || '',
        pb_tradingname: (data.tradingName as string) || '',
        pb_businessdescription: (data.businessDescription as string) || '',
        ot_yourenquiry: (data.message as string) || '',
        // Financing type preference
        pb_financingtype: (data.financingType as string) || '',
        // Addon services (convert array to string if needed)
        pb_addonservices: Array.isArray(data.addonServices) 
          ? (data.addonServices as string[]).join(', ') 
          : (data.addonServices as string) || '',
        // Trust details (if applicable)
        pb_trusttype: (data.trusteeType as string) || '',
        pb_trusteename: (data.trusteeName as string) || '',
        pb_trustabnacn: (data.trustAbnAcn as string) || '',
        // Marketing consent
        ot_howdidyouhearaboutus: (data.howDidYouHear as string) || '',
        pb_referredby: (data.referredBy as string) || '',
        ot_subscribedtonewsletter: (data.marketingConsent ? 'true' : 'false'),
        ot_acceptedtermsconditions: (data.termsAccepted ? 'true' : 'false'),
        // UTM parameters
        ...utmParams,
        // Include reCAPTCHA token (already added by MultiStepForm)
        recaptchaToken: (data.recaptchaToken as string) || '',
      };
      
      // Submit to Dynamics via API route
      const response = await fetch('/api/dynamics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dynamicsPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to submit form');
      }

      console.info('Form submitted successfully:', result);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to let MultiStepForm handle the error display
    }
  },
  showProgress: true,
  allowBackNavigation: true,
  requireRecaptcha: true, // Enable reCAPTCHA v3 protection
  onStepChange: (currentStep, totalSteps) => {
    console.info(`Step ${currentStep} of ${totalSteps}`);
  },
}; 